import React, { useState, useRef } from 'react';
import { Upload, Download, Info, Check, CheckCircle, X, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import { uploadOrganizations } from '@/services/PagesServices/BulkUploadService';
import './OrganizationBulkUploadTab.css';

interface UploadResult {
  totalRows: number;
  inserted: number;
  skipped: number;
  errors: Array<{ row: number; field: string; message: string }>;
  warnings: string[];
}

interface PreviewData {
  [key: string]: string;
}

const OrganizationBulkUploadTab: React.FC = () => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    // Validate file type
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      setError('Invalid file type. Please upload a CSV file.');
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      setError('File is too large. Maximum size is 10MB.');
      return;
    }

    setCsvFile(file);
    setError(null);

    // Parse CSV file for preview
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as PreviewData[];

        if (data.length === 0) {
          setError('CSV file is empty');
          toast.error('CSV file is empty');
          return;
        }

        // Validate required columns
        const headers = Object.keys(data[0]).map(h => h.toLowerCase().replace(/\s+/g, ''));
        const requiredFields = ['name'];

        const missingFields: string[] = [];
        for (const field of requiredFields) {
          if (!headers.includes(field)) {
            missingFields.push(field);
          }
        }

        if (missingFields.length > 0) {
          setError(`Missing required column: ${missingFields.join(', ')}`);
          toast.error(`Missing required column: ${missingFields.join(', ')}`);
          setCsvFile(null);
          return;
        }

        // Set preview data (first 5 rows)
        setPreviewData(data.slice(0, 5));
      },
      error: (error) => {
        const errorMsg = `Failed to parse CSV: ${error.message}`;
        setError(errorMsg);
        toast.error(errorMsg);
        setPreviewData([]);
      }
    });
  };

  const handleCancelUpload = () => {
    setCsvFile(null);
    setPreviewData([]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportOrganizations = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await uploadOrganizations(csvFile);

      // Store upload results
      setUploadResult({
        totalRows: result.data.totalRows,
        inserted: result.data.inserted || 0,
        skipped: result.data.skipped || 0,
        errors: result.data.errors || [],
        warnings: result.data.warnings || [],
      });

      // Show results modal
      setShowResultsModal(true);

      // Reset upload form
      handleCancelUpload();

      const insertedCount = result.data.inserted || 0;
      if (result.success && insertedCount > 0) {
        toast.success(`Upload completed! ${insertedCount} organizations created successfully.`);
      } else if (insertedCount === 0 && result.data.errors.length > 0) {
        toast.error('No organizations were uploaded. Please check the error report.');
      }
    } catch (error: any) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = 'name,is_active\n';
    const csvContent = headers +
      'Acme Corporation,yes\n' +
      'Tech Solutions Inc,yes\n' +
      'Global Industries,no';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'organizations_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Template downloaded successfully');
  };

  const handleDownloadReport = () => {
    if (!uploadResult) return;

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fileName = `organization_upload_report_${timestamp}.csv`;

    // Create report content
    let reportContent = '=== ORGANIZATION BULK UPLOAD REPORT ===\n\n';
    reportContent += `Upload Date: ${new Date().toLocaleString()}\n`;
    reportContent += `Total Rows Processed: ${uploadResult.totalRows}\n`;
    reportContent += `Successfully Uploaded: ${uploadResult.inserted}\n`;
    reportContent += `Failed/Skipped: ${uploadResult.skipped}\n`;
    reportContent += '\n\n';

    // Add errors section
    if (uploadResult.errors.length > 0) {
      reportContent += '=== FAILED ROWS (Errors) ===\n';
      reportContent += 'Row Number,Field,Error Reason\n';
      uploadResult.errors.forEach((error) => {
        reportContent += `${error.row},"${error.field}","${error.message}"\n`;
      });
      reportContent += '\n\n';
    }

    // Add warnings section
    if (uploadResult.warnings.length > 0) {
      reportContent += '=== WARNINGS ===\n';
      uploadResult.warnings.forEach((warning, index) => {
        reportContent += `${index + 1}. ${warning}\n`;
      });
      reportContent += '\n\n';
    }

    // Add success summary
    if (uploadResult.inserted > 0) {
      reportContent += '=== SUCCESS SUMMARY ===\n';
      reportContent += `${uploadResult.inserted} organization(s) were successfully created.\n`;
    }

    // Create and download the file
    const blob = new Blob([reportContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Upload report downloaded successfully');
  };

  return (
    <div className="org-bulk-upload-container">
      <div className="org-bulk-upload-card">
        <div className="org-bulk-upload-card-header">
          <div className="org-bulk-upload-card-icon blue">
            <Upload size={28} />
          </div>
          <div className="org-bulk-upload-card-title">
            <h2>Bulk Import Organizations</h2>
            <p>Upload multiple organizations from CSV file</p>
          </div>
        </div>

        <div className="org-bulk-upload-info-box">
          <div className="org-bulk-upload-info-title">
            <Info size={18} />
            CSV Format Requirements
          </div>
          <ul className="org-bulk-upload-info-list">
            <li><strong>Required Column:</strong> name</li>
            <li><strong>Optional Column:</strong> is_active (yes/no, defaults to yes)</li>
            <li><strong>Example:</strong> name,is_active → Acme Corporation,yes</li>
            <li><strong>Limits:</strong> Max file size 10MB | Max 1000 rows per upload</li>
          </ul>
          <button
            onClick={handleDownloadTemplate}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            <Download size={16} />
            Download Template
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#c33',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={18} />
            <strong>Error:</strong> {error}
          </div>
        )}

        {!csvFile ? (
          <div
            className="org-bulk-upload-upload-area"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <div className="org-bulk-upload-upload-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <path
                  d="M28 14V35M28 14L21 21M28 14L35 21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 35V42C14 43.1046 14.8954 44 16 44H40C41.1046 44 42 43.1046 42 42V35"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="org-bulk-upload-upload-text">
              Drop CSV file here or click to browse
            </div>
            <div className="org-bulk-upload-upload-subtext">Supports .csv files up to 10MB</div>
          </div>
        ) : (
          <>
            {/* File Info */}
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Check size={24} style={{ color: '#16a34a' }} />
                <div>
                  <div style={{ fontWeight: 600, color: '#166534' }}>{csvFile.name}</div>
                  <div style={{ fontSize: '13px', color: '#16a34a' }}>
                    {(csvFile.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              </div>
              <button
                onClick={handleCancelUpload}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#dc2626',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Data Preview */}
            {previewData.length > 0 && (
              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px'
              }}>
                <div style={{ fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                  Data Preview (First 5 rows)
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Organization Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, index) => {
                        const name = row.name || row.Name || '-';
                        const isActive = row.is_active || row.Is_Active || row.isActive || 'yes';

                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px' }}>{name}</td>
                            <td style={{ padding: '8px' }}>
                              {isActive.toLowerCase() === 'yes' || isActive === '1' || isActive.toLowerCase() === 'true' ? (
                                <span style={{ color: '#16a34a' }}>Active</span>
                              ) : (
                                <span style={{ color: '#dc2626' }}>Inactive</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleCancelUpload}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  cursor: 'pointer'
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="org-bulk-upload-submit-btn"
                onClick={handleImportOrganizations}
                disabled={uploading}
                style={{ flex: 1 }}
              >
                <CheckCircle size={18} />
                {uploading ? 'Uploading...' : 'Confirm & Upload'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Upload Results Modal */}
      {showResultsModal && uploadResult && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowResultsModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: uploadResult.inserted > 0 ? '#f0fdf4' : '#fef2f2'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                  Upload Complete
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {uploadResult.totalRows} rows processed
                </p>
              </div>
              <button
                onClick={() => setShowResultsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  border: '2px solid #86efac'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <CheckCircle size={20} style={{ color: '#16a34a' }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#166534' }}>Successfully Uploaded</span>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#16a34a' }}>
                    {uploadResult.inserted}
                  </div>
                  <div style={{ fontSize: '13px', color: '#16a34a', marginTop: '4px' }}>
                    {uploadResult.inserted === 1 ? 'organization created' : 'organizations created'}
                  </div>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  border: '2px solid #fecaca'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <AlertCircle size={20} style={{ color: '#dc2626' }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#991b1b' }}>Failed/Skipped</span>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#dc2626' }}>
                    {uploadResult.skipped}
                  </div>
                  <div style={{ fontSize: '13px', color: '#dc2626', marginTop: '4px' }}>
                    {uploadResult.skipped === 1 ? 'row had errors' : 'rows had errors'}
                  </div>
                </div>
              </div>

              {/* Success Section */}
              {uploadResult.inserted > 0 && (
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <CheckCircle size={18} style={{ color: '#16a34a' }} />
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#166534' }}>
                      Successfully Created Organizations
                    </h4>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#16a34a', lineHeight: '1.6' }}>
                    ✓ {uploadResult.inserted} {uploadResult.inserted === 1 ? 'organization was' : 'organizations were'} successfully created
                  </p>
                </div>
              )}

              {/* Error Section */}
              {uploadResult.errors.length > 0 && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <AlertCircle size={18} style={{ color: '#dc2626' }} />
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#991b1b' }}>
                      Rejected Rows ({uploadResult.errors.length})
                    </h4>
                  </div>
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    fontSize: '13px'
                  }}>
                    {uploadResult.errors.map((error, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          border: '1px solid #fee2e2'
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px' }}>
                          Row {error.row} - {error.field}
                        </div>
                        <div style={{ color: '#991b1b' }}>
                          {error.message}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings Section */}
              {uploadResult.warnings.length > 0 && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde047',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <AlertCircle size={18} style={{ color: '#ca8a04' }} />
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#a16207' }}>
                      Warnings ({uploadResult.warnings.length})
                    </h4>
                  </div>
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    fontSize: '13px'
                  }}>
                    {uploadResult.warnings.map((warning, index) => (
                      <div key={index} style={{ padding: '4px 0', color: '#a16207' }}>
                        • {warning}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleDownloadReport}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
              >
                <Download size={16} />
                Download Report
              </button>
              <button
                onClick={() => setShowResultsModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationBulkUploadTab;
